import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createReservation } from '../../api/reservationApi';
import Logo from '../../componets/Logo';
import imageCompression from 'browser-image-compression';
import { fetchSpaceById } from '../../api/spaceApi';
import { calculateTotalPrice } from '../../utils/Calculate';
import { Helmet } from 'react-helmet-async';

const ReservationForm: React.FC = () => {
  const { spaceId } = useParams<{ spaceId?: string }>(); // Allow spaceId to be optional
  const [formData, setFormData] = useState<{
    title: string;
    startDate: string;
    endDate: string;
    comment: string;
    main_image: File | null;
    additional_images: File[];
    totalPrice: number; // Add totalPrice field
  }>({
    title: '',
    startDate: '',
    endDate: '',
    comment: '',
    main_image: null,
    additional_images: [],
    totalPrice: 0, // Initial total price is 0
  });

  const [spaceDetails, setSpaceDetails] = useState<{ price_per_day: number } | null>(null); // Space details to store price per day
  const navigate = useNavigate();

  // Fetch space details (e.g., price_per_day) when spaceId is available
  useEffect(() => {
    if (spaceId) {
      const fetchSpaceDetails = async () => {
        try {
          const space = await fetchSpaceById(spaceId);
          if (space) {
            setSpaceDetails({ price_per_day: space.price });
          }
        } catch (error) {
          console.error('Error fetching space details:', error);
        }
      };
      fetchSpaceDetails();
    }
  }, [spaceId]);

  // Update the form data when inputs change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Update totalPrice based on startDate, endDate, and price_per_day
  useEffect(() => {
    if (spaceDetails && formData.startDate && formData.endDate) {
      
      const totalPrice = calculateTotalPrice(formData.startDate, formData.endDate, spaceDetails.price_per_day);
      setFormData((prev) => ({
        ...prev,
        totalPrice,
      }));
    }
  }, [formData.startDate, formData.endDate, spaceDetails]);

  const handleImageChange = async (files: FileList | null) => {
    if (!files) return;

    try {
      const compressedBlob = await imageCompression(files[0], {
        maxSizeMB: 0.8,
        maxWidthOrHeight: 800,
        useWebWorker: true,
      });

      const compressedFile = new File([compressedBlob], files[0].name, { type: compressedBlob.type });

      setFormData((prev) => ({
        ...prev,
        main_image: compressedFile,
      }));
    } catch (error) {
      console.error('이미지 업로드 중 오류 발생:', error);
      alert('이미지 처리 중 오류가 발생했습니다.');
    }
  };

  const handleAdditionalImagesChange = async (files: FileList | null) => {
    if (!files) return;
  
    try {
      const compressedFiles: File[] = [];
  
      for (let i = 0; i < Math.min(files.length, 3); i++) {
        const compressedBlob = await imageCompression(files[i], {
          maxSizeMB: 0.8,
          maxWidthOrHeight: 800,
          useWebWorker: true,
        });
        const compressedFile = new File([compressedBlob], files[i].name, { type: compressedBlob.type });
        compressedFiles.push(compressedFile);
      }
  
      setFormData((prev) => ({
        ...prev,
        additional_images: compressedFiles,
      }));
    } catch (error) {
      console.error('추가 이미지 업로드 오류:', error);
      alert('추가 이미지 처리 중 오류가 발생했습니다.');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!spaceId || !formData.totalPrice) {
      alert('잘못된 입력이 있습니다.');
      return;
    }

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('spaceId', spaceId);
      formDataToSend.append('title', formData.title);
      formDataToSend.append('start_date', formData.startDate);
      formDataToSend.append('end_date', formData.endDate);
      formDataToSend.append('comment', formData.comment || '');
      formDataToSend.append('total_price', String(formData.totalPrice));

      if (formData.main_image) {
        formDataToSend.append('main_image', formData.main_image);
      }

      formData.additional_images.forEach((file) => {
        formDataToSend.append('additional_images', file);
      });

      const response = await createReservation(formDataToSend);
      alert('예약이 생성되었습니다!');
      navigate(`/page/ReservationDetail/${response.body.reservation.id}`);
    } catch (error: any) {
      console.error('예약 생성 실패:', error);
      alert('예약 생성에 실패했습니다.');
    }
  };

  return (
    <div className="p-6">
      <Helmet>
        <title>예약 폼 | BIN-GO</title>
        <meta name="description" content="맡기고 싶은 짐을 예약하기 위한 폼입니다. 필요한 정보를 입력하고 예약을 완료하세요." />
        <meta property="og:title" content="예약 폼 | BIN-GO" />
        <meta property="og:description" content="스마트 공간 예약 신청 페이지." />
      </Helmet>
      <Logo />
      <h2 className="text-2xl font-bold mb-4 mt-6">짐 맡기기</h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">물품이름</label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            className="border rounded w-full p-2"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">시작일</label>
          <input
            type="date"
            name="startDate"
            value={formData.startDate}
            onChange={handleChange}
            onFocus={(e) => e.target.showPicker()}
            className="border rounded w-full p-2"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">만료일</label>
          <input
            type="date"
            name="endDate"
            value={formData.endDate}
            onChange={handleChange}
            onFocus={(e) => e.target.showPicker()}
            className="border rounded w-full p-2"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">물건 사진</label>
          <input
            type="file"
            multiple
            onChange={(e) => handleImageChange(e.target.files)}
            className="w-full border border-gray-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <p className="text-sm text-gray-500 mt-2">추가 이미지 (최대 3장)</p>
          <input
            type="file"
            multiple
            onChange={(e) => handleAdditionalImagesChange(e.target.files)}
            className="w-full border border-gray-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        <div>
          <label className="block text-sm font-medium text-gray-700">호스트에게 전할 말</label>
          <textarea
            name="comment"
            value={formData.comment}
            onChange={handleChange}
            className="border rounded w-full p-2"
          />
        </div>
        <button type="submit" className="bg-blue-500 text-white px-4 py-2 rounded">
          짐 맡기기
        </button>
      </form>
    </div>
  );
};

export default ReservationForm;
